import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { AppNavTourStep } from "../../data/appNavTourSteps";
import { C } from "../alignment-advantage/shared/constants";
import { useAppNavTourSteps } from "../../hooks/useAppNavTourSteps";

const PAD = 8;
const TOUR_Z = 120;

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface AppNavTourOverlayProps {
  active: boolean;
  onClose: () => void;
  /** Open mobile nav drawer so sidebar targets are measurable */
  onEnsureNavVisible?: () => void;
  steps?: AppNavTourStep[];
}

function isElementVisible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === "hidden" || style.display === "none") return false;
  if (parseFloat(style.opacity) < 0.05) return false;
  return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight;
}

function measureTarget(targetId: string | undefined): HighlightRect | null {
  if (!targetId) return null;
  const el = document.querySelector(`[data-tour-nav="${targetId}"]`);
  if (!el || !isElementVisible(el)) return null;
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };
}

/**
 * Lightweight spotlight tour for Command Centre sidebar navigation.
 */
const AppNavTourOverlay: React.FC<AppNavTourOverlayProps> = ({
  active,
  onClose,
  onEnsureNavVisible,
  steps: stepsProp,
}) => {
  const tierSteps = useAppNavTourSteps();
  const steps = stepsProp ?? tierSteps;

  const [stepIndex, setStepIndex] = useState(0);
  const [highlight, setHighlight] = useState<HighlightRect | null>(null);

  const visibleSteps = useMemo(() => {
    if (!active) return [];
    return steps;
  }, [active, steps]);

  const currentStep = visibleSteps[stepIndex] ?? null;
  const isFirst = stepIndex <= 0;
  const isLast = stepIndex >= visibleSteps.length - 1;

  const remeasure = useCallback(() => {
    if (!currentStep) {
      setHighlight(null);
      return;
    }
    if (currentStep.targetId) {
      onEnsureNavVisible?.();
      const next = measureTarget(currentStep.targetId);
      setHighlight(next);
      if (next) {
        const el = document.querySelector(
          `[data-tour-nav="${currentStep.targetId}"]`
        );
        el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    } else {
      setHighlight(null);
    }
  }, [currentStep, onEnsureNavVisible]);

  useEffect(() => {
    if (!active) {
      setStepIndex(0);
      setHighlight(null);
      return;
    }
    onEnsureNavVisible?.();
    const id = window.requestAnimationFrame(() => {
      setStepIndex(0);
    });
    return () => window.cancelAnimationFrame(id);
  }, [active, onEnsureNavVisible]);

  useLayoutEffect(() => {
    if (!active || !currentStep) return;
    onEnsureNavVisible?.();
    remeasure();
    const delayed = window.setTimeout(() => remeasure(), 320);
    const onResize = (): void => remeasure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.clearTimeout(delayed);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [active, currentStep, remeasure, onEnsureNavVisible]);

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active, onClose]);

  if (!active || visibleSteps.length === 0 || !currentStep) {
    return null;
  }

  const tooltipStyle: React.CSSProperties = highlight
    ? {
        position: "fixed",
        top: Math.min(highlight.top + highlight.height + 12, window.innerHeight - 200),
        left: Math.min(
          Math.max(16, highlight.left),
          window.innerWidth - 320 - 16
        ),
        maxWidth: 320,
      }
    : {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: 360,
        width: "calc(100% - 32px)",
      };

  const overlay = (
    <div
      className="fixed inset-0"
      style={{ zIndex: TOUR_Z }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-nav-tour-title"
    >
      {/* Dim panels around highlight (click-through blocked on backdrop only) */}
      {highlight ? (
        <>
          <div
            className="fixed left-0 right-0 top-0 bg-black/55"
            style={{ height: Math.max(0, highlight.top) }}
            aria-hidden="true"
          />
          <div
            className="fixed left-0 bg-black/55"
            style={{
              top: highlight.top,
              width: Math.max(0, highlight.left),
              height: highlight.height,
            }}
            aria-hidden="true"
          />
          <div
            className="fixed bg-black/55"
            style={{
              top: highlight.top,
              left: highlight.left + highlight.width,
              right: 0,
              height: highlight.height,
            }}
            aria-hidden="true"
          />
          <div
            className="fixed left-0 right-0 bottom-0 bg-black/55"
            style={{ top: highlight.top + highlight.height }}
            aria-hidden="true"
          />
          <div
            className="fixed pointer-events-none rounded-xl"
            style={{
              top: highlight.top,
              left: highlight.left,
              width: highlight.width,
              height: highlight.height,
              boxShadow: `0 0 0 2px ${C.coral}, 0 0 0 6px ${C.coral}44`,
            }}
            aria-hidden="true"
          />
        </>
      ) : (
        <div className="fixed inset-0 bg-black/55" aria-hidden="true" />
      )}

      <div
        className="rounded-2xl border border-white/10 shadow-2xl p-4 sm:p-5"
        style={{
          ...tooltipStyle,
          zIndex: TOUR_Z + 1,
          background: C.navyMid,
          color: C.white,
        }}
      >
        <p
          className="text-[10px] font-bold uppercase tracking-widest mb-1"
          style={{ color: C.coral }}
        >
          Step {stepIndex + 1} of {visibleSteps.length}
        </p>
        <h2 id="app-nav-tour-title" className="text-base font-bold mb-2">
          {currentStep.title}
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.78)" }}>
          {currentStep.body}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="text-xs font-semibold px-3 py-2 rounded-lg"
            style={{ color: "rgba(255,255,255,0.65)" }}
            onClick={onClose}
          >
            Skip tour
          </button>
          <div className="flex-1" />
          {!isFirst && (
            <button
              type="button"
              className="text-xs font-semibold px-3 py-2 rounded-lg border border-white/15"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="text-xs font-bold px-4 py-2 rounded-lg"
            style={{ background: C.coral, color: C.white }}
            onClick={() => {
              if (isLast) onClose();
              else setStepIndex((i) => i + 1);
            }}
          >
            {isLast ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default AppNavTourOverlay;
